import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Plus, Sparkles, Users, X } from "lucide-react";
import { EmptyState, SectionTitle, Spinner, StatusPill } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, dateRange } from "@/lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1590767602124-234d1714ed92?crop=entropy&cs=srgb&fm=jpg&q=70&w=800";
const FILTERS = ["all", "planning", "upcoming", "in_progress", "completed"];

function Welcome({ onClose }) {
  const steps = [["Create your first trip", "Name, dates and destination — that's it."], ["Invite your people", "Share a link or send email invites."], ["Track costs", "Add expenses and see everyone's personal total."]];
  return (
    <div className="card relative mb-6 overflow-hidden bg-gradient-to-br from-orange-50 via-white to-teal-50 p-6" data-testid="onboarding-card">
      <button onClick={onClose} className="absolute right-4 top-4 rounded-full p-1 hover:bg-white" aria-label="Dismiss" data-testid="onboarding-dismiss"><X className="h-4 w-4" /></button>
      <p className="flex items-center gap-2 text-sm font-semibold text-orange-600"><Sparkles className="h-4 w-4" />Welcome to Triply</p>
      <h2 className="mt-1 text-xl font-bold">Three steps to a calmer group trip</h2>
      <ol className="mt-4 grid gap-3 sm:grid-cols-3">
        {steps.map(([t, d], i) => <li key={t} className="rounded-xl bg-white/80 p-4"><span className="font-mono text-xs text-slate-400">0{i + 1}</span><p className="font-semibold">{t}</p><p className="text-sm text-slate-600">{d}</p></li>)}
      </ol>
    </div>
  );
}

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [trips, setTrips] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/trips").then((r) => setTrips(r.data)); }, []);
  const dismiss = () => { api.post("/onboarding/dismiss"); setUser({ ...user, onboarding: { welcomed: true } }); };
  if (!trips) return <Spinner />;
  const shown = trips.filter((t) => filter === "all" || t.status === filter);
  return (
    <div>
      {!user.onboarding?.welcomed && <Welcome onClose={dismiss} />}
      <SectionTitle title={`Hi ${user.name.split(" ")[0]}, here are your trips`} sub="Unlimited trips on every plan."
        right={<Link to="/trips/new" className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-orange-500/20 hover:bg-orange-600" data-testid="dashboard-new-trip-button"><Plus className="h-4 w-4" />New trip</Link>} />
      {trips.length > 0 && (
        <div className="no-scrollbar mb-5 flex gap-2 overflow-x-auto">
          {FILTERS.map((f) => <button key={f} onClick={() => setFilter(f)} className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold capitalize ${filter === f ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`} data-testid={`trip-filter-${f}`}>{f.replace("_", " ")}</button>)}
        </div>
      )}
      {trips.length === 0 ? (
        <EmptyState icon={MapPin} title="No trips yet — let's fix that" text="Start with a name and rough dates. You can invite people and add costs whenever you're ready."
          action={<Link to="/trips/new" className="rounded-full bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white" data-testid="empty-new-trip-button">Plan my first trip</Link>} />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="trip-list">
          {shown.map((t, i) => (
            <Link key={t.trip_id} to={`/trips/${t.trip_id}`} className="card card-hover rise overflow-hidden" style={{ animationDelay: `${i * 0.04}s` }} data-testid={`trip-card-${t.trip_id}`}>
              <div className="relative h-36"><img src={t.cover_image || FALLBACK} alt="" className="h-full w-full object-cover" />
                <span className="absolute left-3 top-3"><StatusPill status={t.status} /></span></div>
              <div className="p-5">
                <h3 className="text-lg font-bold">{t.name}</h3>
                <div className="mt-2 space-y-1 text-sm text-slate-600">
                  {t.destination && <p className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{t.destination}</p>}
                  <p className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />{dateRange(t.start_date, t.end_date)}</p>
                  <p className="flex items-center gap-1.5"><Users className="h-4 w-4" />{t.traveler_count} traveler{t.traveler_count !== 1 ? "s" : ""} · <span className="capitalize">{t.my_role.replace("_", "-")}</span></p>
                </div>
              </div>
            </Link>
          ))}
          {shown.length === 0 && <p className="text-sm text-slate-500">No trips with this status.</p>}
        </div>
      )}
    </div>
  );
}
